import { AssetsUserDetailsItemComponentBase } from './assets-user-details-item.component.base';
import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Authority, CrudEditorParentService, CrudEditorSelfService, dateFormat, onValueChangeEvent, requiredValidator, User, USER_DETAILS_ATTRIBUTES_METADATA, USER_DETAILS_WITH_ATTRIBUTES_METADATA, UserDetailsWithAttributes, UserService, UserStorage } from '@scisys-uim/uim-common';
import { BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, isUserBusinessUnitActor, isUserNationalActor } from 'assets-model';
import { AssetsUserDetailsService, AssetsUserStorage, BusinessUnitService, CostCentreService } from 'assets-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

interface UserAttributeDisplay {
  attributeName: string;
  attributeValue: any;
}

function compareUserAttributes(a: UserAttributeDisplay, b: UserAttributeDisplay) {
  if (a.attributeName == 'authorities' && b.attributeName != 'authorities') {
    return 1;
  }
  if (a.attributeName != 'authorities' && b.attributeName == 'authorities') {
    return -1;
  }
  if (a.attributeName < b.attributeName) {
    return -1;
  }
  if (a.attributeName > b.attributeName) {
    return 1;
  }
  return 0;
}

@Component({
  selector: 'app-assets-user-details-item',
  templateUrl: './assets-user-details-item.component.html',
  styleUrls: ['./assets-user-details-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class AssetsUserDetailsItemComponent extends AssetsUserDetailsItemComponentBase {

  private linkedUser: User;
  private nationalActor: boolean;
  private businessUnitActor: boolean;

  userDetailsWithAttributes: UserDetailsWithAttributes;

  userDetailsWithAttributesDisplayedColumns = ['attributeName', 'attributeValue'];
  userDetailsWithAttributesDataSource: { attributeName: string, attributeValue: any }[];

  allCostCentres: CostCentre[];
  @ViewChild('costCentres') costCentresInput: ElementRef;
  private costCentresSelectionListSubject: Subject<CostCentre[]> = new BehaviorSubject([]);
  costCentresSelectionList: Observable<CostCentre[]> = this.costCentresSelectionListSubject.asObservable();

  constructor(protected assetsUserDetailsService: AssetsUserDetailsService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected userDetailsStorage: AssetsUserStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected userService: UserService,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(assetsUserDetailsService, businessUnitService, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  createForm(): FormGroup {
    return this.fb.group({
      user: [null],
      businessUnit: [null, [requiredValidator]],
      costCentres: [null],
      landline: [null],
      notificationSummaryEmails: [null],
      mobile: [null]
    });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    if (this.costCentres) {
      onValueChangeEvent(this.costCentres, value => {
        if (value == "") {
          value = null;
          // if (this.item) {
          //   this.item.costCentres = null;
          // }
        }
        if (value == null || typeof value == 'string') {
          this.costCentresSelectionListFilter(value);
        }
      });
    }
  }

  getExcludedByExistingUserDetailsTypes(): string[] {
    return this.assetsUserDetailsService.getUserDetailsExcludedByExistingUserDetailsTypes(this.userDetailsWithAttributes);
  }

  isExcludedByExistingUserDetailsType(): boolean {
    return this.assetsUserDetailsService.isUserDetailsExcludedByExistingUserDetailsType(this.userDetailsWithAttributes);
  }

  itemSet() {
    super.itemSet();
    var username = this.item?.user;
    if (username != null) {
      this.userService.get(username).subscribe(user => {
        this.linkedUser = user;
        this.nationalActor = isUserNationalActor(this.linkedUser);
        this.businessUnitActor = isUserBusinessUnitActor(this.linkedUser);
        this.businessUnitLoadSelectionList();
        this.costCentresLoadSelectionList();

        this.assetsUserDetailsService.findUserDetailsWithAttributesByUsername(username).subscribe(item => {
          this.userDetailsWithAttributes = item;
          if (this.isExcludedByExistingUserDetailsType()) {
            this.readOnly = true;
          }
          this.userDetailsWithAttributesDataSource = this.createUserDetailsWithAttributesDataSource();
        });
      });
    }
  }

  setDefaults() {
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    this.user.disable();
  }

  costCentresDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return item.ccId + " - " + item.name;
  }


  selectedCostCentre(event: MatAutocompleteSelectedEvent): void {
    var newItem: CostCentre = this.allCostCentres.find(p => p.ccId === event.option.value.ccId);
    if (this.item.costCentres) {
      this.item.costCentres.push(newItem);
    } else {
      this.item.costCentres = [newItem];
    }
    this.costCentresInput.nativeElement.value = '';
    this.costCentres.setValue(null);
    this.costCentresSelectionListFilter(null);
  }

  costCentresLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.allCostCentres = data.items;
        this.costCentresSelectionListFilter(null);
      }
    );
  }

  costCentresSelectionListFilter(text: string): CostCentre[] {
    var filtered = [];

    this.costCentres.disable();
    if (!this.nationalActor && !this.businessUnitActor && this.linkedUser && !this.linkedUser?.systemProvided) {
      if (this.item.businessUnit) {
        // only allow CostCentres from the selected businessUnit
        filtered = this.allCostCentres || [];
        filtered = filtered.filter(p => p.businessUnit.buId == this.item.businessUnit.buId);
      }

      // include only ones already selected
      filtered = filtered.filter(p => !(this.item.costCentres || []).find(o => o.name == p.name));

      if (filtered.length > 0 && !this.readOnly) {
        this.costCentres.enable();
      }

      // include only ones matching text input
      if (text != null && text.length > 0) {
        filtered = filtered.filter(p => p.name.toLowerCase().includes(text.toLowerCase()));
      }
    }

    this.costCentresSelectionListSubject.next(filtered);
    return filtered;
  }

  costCentresCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }

  removeCostCentre(costCentre: CostCentre): void {
    const index = this.item.costCentres.indexOf(costCentre);

    if (index >= 0) {
      this.item.costCentres.splice(index, 1);
      this.costCentresSelectionListFilter(null);
    }
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        var businessUnits = data.items;
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, businessUnits);
      }
    );
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    var filteredCostCentres = this.costCentresSelectionListFilter(null);
    if (filteredCostCentres && filteredCostCentres.length > 0 && !this.readOnly) {
      if (this.item.businessUnit) {
        if (this.item) {
          this.item.costCentres = this.item.costCentres.filter(b => b.businessUnit.buId == this.item.businessUnit.buId);
        }
      } else {
        if (this.item) {
          this.item.costCentres = [];
        }
      }
    }
  }


  get userDisplayName(): string {
    const user = this.userDetailsWithAttributes;
    if (!user) {
      return '';
    }
    const displayName = user.displayName;
    if (displayName == user.username) {
      return '';
    }
    return displayName;
  }

  showUserDetailsWithAttributes(): boolean {
    return this.debugService.debugEnabled && this.userDetailsWithAttributesDataSource != null;
  }

  createUserDetailsWithAttributesDataSource(): { attributeName: string; attributeValue: any; }[] {
    const result: { attributeName: string; attributeValue: any; }[] = [];
    const excludedProperties = ['attributes'];
    var properties = USER_DETAILS_ATTRIBUTES_METADATA.properties;
    const attributeProperties = [];
    if (!this.userDetailsWithAttributes) {
      return result;
    }
    Object.keys(properties).filter(prop => !excludedProperties.includes(prop)).forEach(key => {
      const property = properties[key];
      attributeProperties.push(property.name);
      const value = this.userDetailsWithAttributes.attributes[key];
      if (value != null) {
        result.push({ attributeName: property.name, attributeValue: value });
      }
    });

    properties = USER_DETAILS_WITH_ATTRIBUTES_METADATA.properties;
    Object.keys(properties).filter(prop => !excludedProperties.includes(prop)).filter(prop => !attributeProperties.includes(prop)).forEach(key => {
      const property = properties[key];
      const value = this.userDetailsWithAttributes[key];
      if (value != null) {
        result.push({ attributeName: property.name, attributeValue: value });
      }
    });

    result.push({ attributeName: 'displayName', attributeValue: this.userDetailsWithAttributes.displayName });

    result.sort(compareUserAttributes);
    return result;
  }

  displayAttributeValue(name: string, value: any) {
    if (value instanceof Date) {
      return dateFormat(value);
    }
    if (value instanceof BusinessUnit) {
      return BUSINESS_UNIT_TYPE.description(value);
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        if (value[0] instanceof CostCentre) {
          return '[' + value.map((costCentre: CostCentre) => COST_CENTRE_TYPE.description(costCentre)).join(', ') + ']';
        }
        if (value[0].authority != null) {
          return '[' + value.map((authority: Authority) => authority.authority).join(', ') + ']';
        }
        if (typeof value[0] == 'string') {
          return '[' + value.join(', ') + ']';
        }
      } else {
        return '[]';
      }
    }
    return value;
  }

  get costCentres() {
    return this.control('costCentres');
  }
}





